import CryptoJS from 'crypto-js'
import { consoleLog, consoleError } from '@/utils/logger'

// Clé secrète pour le cryptage (en production, utiliser une variable d'environnement)
const SECRET_KEY = process.env.VUE_APP_CRYPTO_SECRET || 'peelochat-dashboard-secret-key-2024' 

/**
 * Crypte des données utilisateur pour la transmission sécurisée
 * @param {Object} data - Les données à crypter
 * @returns {string} - Données cryptées en base64 URL-safe
 */
export function encryptUserData(data) {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data)
    consoleLog('🔐 [Crypto] Début du cryptage des données utilisateur')
    
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString()
    consoleLog('✅ [Crypto] Données cryptées avec AES, longueur:', encrypted.length)
    
    // Le résultat de CryptoJS.AES.encrypt().toString() est déjà en base64
    // Pas besoin de double encodage avec btoa/Buffer
    // Juste convertir en URL-safe
    const urlSafe = encrypted
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '')
    
    consoleLog('✅ [Crypto] Token URL-safe généré, longueur:', urlSafe.length)
    
    return urlSafe
  } catch (error) {
    consoleError('❌ [Crypto] Erreur lors du cryptage:', error)
    throw new Error('Impossible de crypter les données utilisateur')
  }
}

/**
 * Décrypte des données utilisateur reçues
 * @param {string} encryptedData - Données cryptées en base64 (URL-safe ou standard)
 * @returns {Object} - Données décryptées
 */
export function decryptUserData(encryptedData) {
  try {
    consoleLog('🔓 [Crypto] Début du décryptage, longueur du token:', encryptedData?.length)
    
    // Détecter si c'est du base64 URL-safe ou standard
    const isUrlSafe = !encryptedData.includes('+') && !encryptedData.includes('/') && !encryptedData.includes('=')
    
    let base64 = encryptedData
    
    if (isUrlSafe) {
      // Si c'est URL-safe, reconvertir vers base64 standard
      consoleLog('🔄 [Crypto] Détection: format URL-safe, reconversion en base64 standard...')
      base64 = encryptedData
        .replace(/-/g, '+')
        .replace(/_/g, '/')
      
      // Ajouter le padding nécessaire
      while (base64.length % 4) {
        base64 += '='
      }
    } else {
      // Si c'est déjà en base64 standard, l'utiliser tel quel
      consoleLog('✅ [Crypto] Détection: format base64 standard, utilisation directe')
      base64 = encryptedData
    }
    
    consoleLog('✅ [Crypto] Token prêt pour décryptage, longueur:', base64.length)
    
    // Décrypter avec AES
    const decryptedBytes = CryptoJS.AES.decrypt(base64, SECRET_KEY)
    const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8)
    
    consoleLog('✅ [Crypto] Décryptage AES effectué, longueur:', decryptedString?.length)
    
    if (!decryptedString) {
      consoleError('❌ [Crypto] Le décryptage a produit une chaîne vide')
      throw new Error('Décryptage échoué - données corrompues ou clé incorrecte')
    }
    
    const parsedData = JSON.parse(decryptedString)
    consoleLog('✅ [Crypto] Données JSON parsées avec succès')
    consoleLog('📦 [Crypto] Clés présentes:', Object.keys(parsedData))
    
    return parsedData
  } catch (error) {
    consoleError('❌ [Crypto] Erreur lors du décryptage:', error)
    consoleError('❌ [Crypto] Type d\'erreur:', error.name)
    consoleError('❌ [Crypto] Message:', error.message)
    
    if (error instanceof SyntaxError) {
      throw new Error('Impossible de décrypter - format JSON invalide')
    }
    
    throw new Error('Impossible de décrypter les données utilisateur - token invalide ou expiré')
  }
}

/**
 * Valide que les données décryptées sont bien des données utilisateur valides
 * @param {Object} userData - Données utilisateur à valider
 * @returns {boolean} - True si les données sont valides
 */
export function validateUserData(userData) {
  consoleLog('🔍 [Crypto] Validation des données utilisateur...')
  
  if (!userData) {
    consoleError('❌ [Crypto] Validation échouée: userData est null ou undefined')
    return false
  }
  
  if (typeof userData !== 'object') {
    consoleError('❌ [Crypto] Validation échouée: userData n\'est pas un objet')
    return false
  }
  
  if (!userData.data) {
    consoleError('❌ [Crypto] Validation échouée: userData.data est manquant')
    return false
  }
  
  if (!userData.data.token && !userData.data.userId) {
    consoleError('❌ [Crypto] Validation échouée: ni token ni userId trouvé')
    return false
  }
  
  consoleLog('✅ [Crypto] Validation réussie:', {
    hasToken: !!userData.data.token,
    hasUserId: !!userData.data.userId,
    hasEmail: !!userData.data.email,
    hasName: !!userData.data.name
  })
  
  return true
} 